import {
  Injectable,
  Inject,
  Optional,
  PLATFORM_ID,
  Renderer2,
  TransferState,
  makeStateKey,
  inject,
  signal,
  DOCUMENT,
} from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Request } from 'express';
import { ReplaySubject } from 'rxjs';
import { CookieConsentModel } from '../models/cookie-consent.model';
import { SettingsService } from './settings.service';

declare let gtag: Function;
declare const ga: any;
export interface AnalyticsEvent {
  type: 'PAGEVIEW' | 'EVENT';
  category?: string;
  action?: string;
  label: string;
}

@Injectable({
  providedIn: 'root',
})
export class CookieConsentService {
  settingsService = inject(SettingsService);
  platformId = inject(PLATFORM_ID);
  document = inject(DOCUMENT);

  public showConsent = signal<boolean>(false);
  public consent = signal<CookieConsentModel>(new CookieConsentModel());

  eventsQueue$ = new ReplaySubject<AnalyticsEvent>();

  consentSentToGtag = false;
  tagManagerLoaded = false;
  tagManagerLoadScheduled = false;
  isCrawlerVisitor = false;

  private static readonly CRAWLER_STATE_KEY = makeStateKey<boolean>(
    'cookie-consent:isCrawler',
  );
  private static readonly CRAWLER_USER_AGENT_REGEX =
    /bot|crawler|spider|slurp|search|bing|duckduck|baidu|yandex|sogou|facebot|facebookexternalhit|ia_archiver|linkedinbot|mediapartners-google|preview|embedly/i;
  private transferState = inject(TransferState, { optional: true });

  constructor(@Optional() @Inject('REQUEST') private request: Request) {
    this.isCrawlerVisitor = this.resolveCrawlerState();

    const hasConsent = this.isCrawlerVisitor
      ? new CookieConsentModel()
      : this.isBrowser()
        ? this.checkConsentFromStorage('constructor')
        : new CookieConsentModel();
    this.consent.set(hasConsent);

    if (this.isCrawlerVisitor) {
      this.showConsent.set(false);
    }
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  public checkConsentFromStorage(from: string): CookieConsentModel {
    if (this.isCrawlerVisitor) {
      this.showConsent.set(false);
      return new CookieConsentModel();
    }

    if (isPlatformServer(this.platformId)) {
      this.showConsent.set(false);

      return new CookieConsentModel();
    }

    if (isPlatformBrowser(this.platformId)) {
      const cookie = this.getCookie('cookie-consent');
      if (cookie) {
        const cookieModel: CookieConsentModel = JSON.parse(cookie);

        if (
          cookieModel.Version ===
          this.settingsService.setting().Privacy.CookieVersion
        ) {
          this.showConsent.set(false);

          this.updateConsent(cookieModel, 'checkConsentFromStorage 2');

          return cookieModel;
        } else {
          this.showConsent.set(true);
        }
      } else {
        this.showConsent.set(true);
      }
    }

    return new CookieConsentModel();
  }

  public deleteCookie(cookieName: string) {
    this.setCookie(cookieName, '', -1);
  }

  public setCookie(
    cookieName: string,
    cookieValue: string,
    expireDays?: number,
  ) {
    let d: Date = new Date();
    d.setTime(d.getTime() + (expireDays ?? 30) * 24 * 60 * 60 * 1000);
    let expires: string = 'expires=' + d.toUTCString();
    document.cookie =
      cookieName + '=' + cookieValue + ';' + expires + ';path=/';
  }

  private getCookie(cookieName: string): string | null {
    let name = cookieName + '=';
    let decodedCookie = decodeURIComponent(document.cookie);
    let ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }
    return null;
  }

  updateConsent(
    consents: CookieConsentModel,
    _from: string = '',
    hideConsent = true,
  ) {
    if (this.isCrawlerVisitor) {
      return;
    }

    window.dataLayer = window.dataLayer || [];

    consents.Version = this.settingsService.setting().Privacy.CookieVersion;

    this.setCookie('cookie-consent', JSON.stringify(consents), 365);

    this.syncConsentWithTagManager(consents);

    this.consentSentToGtag = true;

    this.consent.set({ ...consents });

    if (hideConsent) {
      this.showConsent.set(false);
    }
  }

  public getConsent(): boolean {
    return this.showConsent();
  }

  public syncConsentWithTagManager(consents: CookieConsentModel) {
    if (
      this.isCrawlerVisitor ||
      !isPlatformBrowser(this.platformId) ||
      typeof window.gtag !== 'function'
    ) {
      return;
    }

    window.gtag('consent', 'update', {
      ad_storage: consents.Marketing ? 'granted' : 'denied',
      analytics_storage: consents.Analytics ? 'granted' : 'denied',
      ad_user_data: consents.Marketing ? 'granted' : 'denied',
      functionality_storage: consents.Functional ? 'granted' : 'denied',
      ad_personalization: consents.Marketing ? 'granted' : 'denied',
    });
  }

  private resolveCrawlerState(): boolean {
    if (isPlatformServer(this.platformId)) {
      const isCrawler = this.detectCrawlerFromRequest();
      this.transferState?.set(
        CookieConsentService.CRAWLER_STATE_KEY,
        isCrawler,
      );
      return isCrawler;
    }

    if (isPlatformBrowser(this.platformId)) {
      return (
        this.transferState?.get(
          CookieConsentService.CRAWLER_STATE_KEY,
          false,
        ) ?? false
      );
    }

    return false;
  }

  private detectCrawlerFromRequest(): boolean {
    if (!this.request) {
      return false;
    }

    const userAgent = this.request.get('user-agent') ?? '';
    return CookieConsentService.CRAWLER_USER_AGENT_REGEX.test(userAgent);
  }

  loadTagManagerScript(googleTagManagerId: string, renderer2: Renderer2) {
    if (
      !googleTagManagerId ||
      this.isCrawlerVisitor ||
      !isPlatformBrowser(this.platformId) ||
      this.tagManagerLoaded ||
      this.tagManagerLoadScheduled
    ) {
      return;
    }

    this.tagManagerLoadScheduled = true;

    this.runWhenIdle(() => {
      this.appendTagManagerScript(googleTagManagerId, renderer2);
    });
  }

  private appendTagManagerScript(
    googleTagManagerId: string,
    renderer2: Renderer2,
  ) {
    if (this.tagManagerLoaded) {
      return;
    }

    const script = renderer2.createElement('script') as HTMLScriptElement;

    script.text = `
    window.dataLayer = window.dataLayer || [];

    window.gtag = function() {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(arguments);
    };

    window.gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
      functionality_storage: 'denied',
    });

    (function(w,d,s,l,i){
      w[l] = w[l] || [];
      w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});

      var f = d.getElementsByTagName(s)[0],
          j = d.createElement(s),
          dl = l !== 'dataLayer' ? '&l=' + l : '';

      j.async = true;
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
      f.parentNode.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', '${googleTagManagerId}');
  `;

    this.tagManagerLoaded = true;
    renderer2.appendChild(this.document.head, script);
    this.syncConsentWithTagManager(this.consent());
  }

  private runWhenIdle(callback: () => void): void {
    const scheduleIdle = () => {
      const requestIdleCallback = window.requestIdleCallback;

      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(callback, { timeout: 5000 });
        return;
      }

      window.setTimeout(callback, 3000);
    };

    if (document.readyState === 'complete') {
      scheduleIdle();
      return;
    }

    window.addEventListener('load', scheduleIdle, { once: true });
  }

  private subscribeToEvents(): void {
    this.eventsQueue$.subscribe((e: AnalyticsEvent) => {
      if (e.type === 'PAGEVIEW') {
        ga('send', {
          hitType: 'pageview',
          page: e.label,
        });
      } else if (e.type === 'EVENT') {
        ga('send', {
          hitType: 'event',
          eventCategory: e.category,
          eventAction: e.action,
          eventLabel: e.label,
        });
      }
    });
  }
}
